import { observable, action, computed, makeObservable, reaction, runInAction } from 'mobx';
import * as Sentry from '@sentry/react';
import type ft from 'firebase';

import { FieldValue, fn, db as firestoreDb } from 'firebase-modules';

import {
  FirestoreSession,
  SessionPermissions,
  safeBatch,
  moduleColors,
  SessionModuleDisplaySettings,
} from '@creative-kit/shared';
import { monotonicFactory, ulid } from 'ulid';
import { getSessionSharingInfo } from 'api/getSessionSharingInfo';
import { COMMON_SOURCE_TYPES } from 'ui/Onboarding/sourcePresets';
import { db } from './data';
import { Session } from './session';
import type { RootStore } from '.';
import { RouteNames } from './routes';
import { trackAction } from './analytics';
import { withStartupSpan, finishStartupTracing } from './startupTracing';

// LayoutPreset type definition (not exported from shared yet)
interface LayoutPreset {
  name: string;
  moduleIds: string[];
  visibility: {
    hiddenModuleIds: string[];
    hideFilename: boolean;
  };
  moduleDisplaySettings: Record<string, SessionModuleDisplaySettings>;
  connectionDisplaySettings?: Record<string, unknown>;
  createdAt: Date;
}

interface SessionOwnerData {
  name: string;
  email: string;
}

/** Eligible new owner from `getSessionOwnershipTransferCandidates` (CRE-252 Spike 4). */
export interface SessionOwnershipTransferCandidate {
  userId: string;
  name: string;
  email: string | null;
}

function reportSessionPermissionLoadFailure(sessionId: string, error: unknown): void {
  // eslint-disable-next-line no-console
  console.error('Failed to load user permissions:', error);
  Sentry.captureException(error, {
    tags: { area: 'sessions', operation: 'loadUserPermissions' },
    extra: { sessionId },
  });
}

export enum SessionFilter {
  ALL = 'all',
  MINE = 'mine',
  SHARED = 'shared',
  ORG = 'org',
  EXTERNAL = 'external',
}

export class SessionsStore {
  private rootStore: RootStore;
  private stopListeningToSessions = () => {};
  private stopListeningToCurrentSession = () => {};
  private stopListeningToSharedSessions = () => {};
  private stopListeningToOrgIdSharedSessions = () => {};
  private stopListeningToDirectShareSessions = () => {};
  private stopListeningToUserHiddenSessions = () => {};
  private lastSharedSessionsLog: { organizationId: string; count: number } | null = null;
  private lastLoggedCurrentSessionId: string | null = null;
  private previousSessionId: string | null = null;
  importInProgress = false;
  /** 403/404 = access or missing; `deleted` = soft-deleted session direct load guard */
  currentSessionError?: 404 | 403 | 'deleted';
  /** When currentSessionError === 'deleted', drives NoSessionView copy/CTAs */
  currentSessionDeletedBlock?:
    | 'owner'
    | 'owner_and_cq_admin'
    | 'collaborator'
    | 'cq_admin_non_owner';
  currentSessionOwner?: SessionOwnerData;
  orderedSessions: Session[] = [];
  sessions: Record<string, Session> = {};
  sharedSessions: Record<string, Session> = {};
  currentFilter: SessionFilter = SessionFilter.ALL;
  directShareSessionIds: string[] = [];
  organizationNames: Record<string, string> = {};
  /** Current user's `users/{uid}.hiddenSessions` (Spike 3); empty for guests */
  hiddenSessionIds: string[] = [];

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      sessions: observable,
      sharedSessions: observable,
      orderedSessions: observable,
      importInProgress: observable,
      currentSessionError: observable,
      currentSessionDeletedBlock: observable,
      currentFilter: observable,
      directShareSessionIds: observable,
      organizationNames: observable,
      hiddenSessionIds: observable,
      liveSessions: computed,
      deletedSessions: computed,
      activeSessions: computed,
      archivedSessions: computed,
      filteredSessions: computed,
      filteredActiveSessions: computed,
      filteredArchivedSessions: computed,
      currentSession: computed,
      currentSessionId: computed,
      updateSessions: action.bound,
      importSession: action.bound,
      resetSessions: action,
      watchCurrentSession: action,
      watchSharedSessions: action,
      setCurrentFilter: action,
      currentSessionOwner: observable,
      setCurrentSessionOwner: action,
      applyDeletedSessionBlock: action.bound,
      restoreSoftDeletedSession: action.bound,
      hideSession: action.bound,
      unhideSession: action.bound,
      requestSessionDeletion: action.bound,
      getSessionOwnershipTransferCandidates: action.bound,
      transferSessionOwnership: action.bound,
      watchHiddenSessionsForUser: action.bound,
    });

    this.rootStore = rootStore;
  }

  init() {
    reaction(() => this.rootStore.authStore.user, this.watchSessions, {
      fireImmediately: true,
    });
    reaction(() => this.rootStore.authStore.user, this.watchSharedSessions, {
      fireImmediately: true,
    });
    reaction(() => this.rootStore.authStore.user, this.watchHiddenSessionsForUser, {
      fireImmediately: true,
    });
    reaction(() => this.currentSessionId, this.watchCurrentSession, {
      fireImmediately: true,
    });
    // If projects list listener updates deletedAt before watchCurrentSession finishes, block the UI.
    reaction(
      () => {
        const sessionId = this.currentSessionId;
        if (!sessionId) return null;
        return this.sessions[sessionId]?.data.deletedAt ?? null;
      },
      (deletedAt) => {
        if (!deletedAt || this.currentSessionError === 'deleted') return;
        const sessionId = this.currentSessionId;
        const sessionData = sessionId ? this.sessions[sessionId]?.data : undefined;
        if (sessionData && SessionsStore.sessionHasDeletedAt(sessionData)) {
          this.applyDeletedSessionBlock(sessionData);
        }
      },
    );
    // Persist last session route per user (localStorage is shared across accounts on the same browser)
    reaction(
      () => ({
        sessionId: this.currentSessionId,
        userId: this.rootStore.authStore.user?.uid,
      }),
      ({ sessionId, userId }) => {
        if (sessionId && userId) {
          try {
            const route = this.rootStore.routerStore.routerState;
            localStorage.setItem(
              `cq:lastSessionRoute:${userId}`,
              JSON.stringify({
                routeName: route.routeName,
                params: route.params ?? {},
              }),
            );
          } catch {
            // localStorage may be unavailable
          }
        }
      },
    );
    // Re-trigger watchCurrentSession when auth changes (especially for guest auth)
    // This ensures session listeners restart with correct auth after guest sign-in
    // We need to reset the session object so dependent stores (sources, cards) also restart
    reaction(
      () => this.rootStore.authStore.user?.isGuest,
      async (isGuest, wasGuest) => {
        if (this.currentSessionId && isGuest !== wasGuest) {
          // Stop current listeners
          this.stopListeningToCurrentSession();
          // Remove session from store so it gets recreated with new auth
          const sessionId = this.currentSessionId;
          if (this.sessions[sessionId]) {
            this.sessions[sessionId].dispose();
            delete this.sessions[sessionId];
          }
          // Wait a moment for Firestore to recognize the new auth token
          // This is necessary because Firestore SDK may cache the previous token
          await new Promise((resolve) => setTimeout(resolve, 500));
          // Only restart if user is still viewing the same session (avoid stale load after navigate)
          if (this.currentSessionId === sessionId) {
            this.watchCurrentSession();
          }
        }
      },
    );
  }

  setCurrentSessionOwner(owner?: SessionOwnerData) {
    this.currentSessionOwner = owner;
  }

  /** Block loading a soft-deleted session into the active session UI (direct URL / refresh). */
  private static sessionHasDeletedAt(data: FirestoreSession | undefined): boolean {
    return data?.deletedAt != null;
  }

  applyDeletedSessionBlock(sessionData: FirestoreSession) {
    const sessionId = this.currentSessionId;
    if (!sessionId) return;
    const { user } = this.rootStore.authStore;
    const isOwner = Boolean(user?.uid && sessionData.userId === user.uid);
    const isCQAdmin = user?.isCQAdmin === true;
    if (isOwner && isCQAdmin) {
      this.currentSessionDeletedBlock = 'owner_and_cq_admin';
    } else if (isOwner) {
      this.currentSessionDeletedBlock = 'owner';
    } else if (isCQAdmin) {
      this.currentSessionDeletedBlock = 'cq_admin_non_owner';
    } else {
      this.currentSessionDeletedBlock = 'collaborator';
    }
    this.currentSessionError = 'deleted';
    const existing = this.sessions[sessionId];
    if (existing) {
      existing.dispose();
      delete this.sessions[sessionId];
    }
    this.stopListeningToCurrentSession();
  }

  // Make sure current session id is in the store to support viewing other users sessions
  watchCurrentSession = async () => {
    this.stopListeningToCurrentSession();
    const shouldFinishStartupTracing = Boolean(this.currentSessionId);

    try {
      const cachedSession = this.currentSessionId
        ? this.sessions[this.currentSessionId]
        : undefined;
      if (SessionsStore.sessionHasDeletedAt(cachedSession?.data)) {
        this.applyDeletedSessionBlock(cachedSession!.data);
        return;
      }

      // Track if session changed for log deduplication
      if (this.currentSessionId && this.lastLoggedCurrentSessionId !== this.currentSessionId) {
        this.lastLoggedCurrentSessionId = this.currentSessionId;
      }

      // Dispose of the previous session's reaction to prevent memory leaks
      if (this.previousSessionId && this.previousSessionId !== this.currentSessionId) {
        const previousSession = this.sessions[this.previousSessionId];
        if (previousSession) {
          previousSession.dispose();
        }
      }

      // For guest users, wait a moment for auth store to initialize after page load
      // This ensures the guest user is recognized before trying to load the session
      const { user } = this.rootStore.authStore;

      // Check if we're on a share route - if so, skip the initial load and let
      // SessionShareView handle the auth flow, then our reaction will restart the load
      const currentPath = window.location.pathname;
      const isShareRoute = currentPath.includes('/share/');
      if (isShareRoute && !user?.isGuest) {
        return; // Don't load session yet - SessionShareView will handle auth, then our reaction restarts
      }

      if (!user && this.currentSessionId) {
        // Wait for auth to initialize (max 2 seconds)
        let attempts = 0;
        while (attempts < 20 && !this.rootStore.authStore.user) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          attempts += 1;
        }
      }

      const updateCurrentSession = (snapshot: ft.firestore.DocumentSnapshot<FirestoreSession>) => {
        runInAction(() => {
          const sessionData = snapshot.data();
          if (!sessionData) {
            return;
          }
          if (
            SessionsStore.sessionHasDeletedAt(sessionData) &&
            snapshot.id === this.currentSessionId
          ) {
            this.applyDeletedSessionBlock(sessionData);
            return;
          }
          if (!this.sessions[snapshot.id]) {
            this.sessions[snapshot.id] = new Session(snapshot.id, sessionData, this.rootStore);
          } else {
            this.sessions[snapshot.id].updateData(sessionData);
          }
          // Ensure the current session's reaction is active and load permissions
          const currentSession = this.sessions[snapshot.id];
          if (currentSession && snapshot.id === this.currentSessionId) {
            currentSession.ensureReactionActive();
            // Load user permissions for the current session (non-blocking)
            // loadUserPermissions() checks internally if already loaded, so safe to call multiple times
            currentSession.loadUserPermissions().catch((error) => {
              reportSessionPermissionLoadFailure(snapshot.id, error);
            });
          }
        });
      };

      if (this.currentSessionId) {
        const currentSessionRef = db.sessions.doc(this.currentSessionId);
        const { user } = this.rootStore.authStore;
        const sessionId = this.currentSessionId;

        // Direct/org shares are preloaded into sharedSessions for the projects list.
        // Promote immediately so navigation does not block on a server-only get (can hang in emulator / slow networks).
        const preloadedShared = this.sharedSessions[sessionId];
        if (preloadedShared && !SessionsStore.sessionHasDeletedAt(preloadedShared.data)) {
          runInAction(() => {
            this.sessions[sessionId] = preloadedShared;
            this.currentSessionError = undefined;
            this.currentSessionDeletedBlock = undefined;
            this.previousSessionId = sessionId;
          });
          preloadedShared.ensureReactionActive();
          preloadedShared.loadUserPermissions().catch((error) => {
            reportSessionPermissionLoadFailure(sessionId, error);
          });
          // onSnapshot + background server get both call updateCurrentSession; order is not guaranteed
          // but both apply the same session payload idempotently.
          this.stopListeningToCurrentSession = currentSessionRef.onSnapshot(updateCurrentSession);

          void currentSessionRef
            .get({ source: 'server' })
            .then((snapshot) => {
              if (snapshot.exists) {
                updateCurrentSession(snapshot);
              }
            })
            .catch((error) => {
              console.error('[SessionsStore] Background session refresh failed:', error);
            });
          return;
        }

        // Cache-first with realtime listener — avoid blocking direct-link opens on server-only reads
        // (can hang indefinitely when Firestore network requests abort on slow/blocked networks).
        this.stopListeningToCurrentSession = currentSessionRef.onSnapshot(updateCurrentSession);

        // startup.session.fetch span — outer finally closes startup tracing for all session-load paths.
        try {
          const session = await withStartupSpan(
            'startup.session.fetch',
            'Fetch current session document',
            () => currentSessionRef.get(),
            { sessionId },
          );
          if (session.exists === false) {
            this.currentSessionError = 404;
          } else {
            const initialData = session.data();
            if (SessionsStore.sessionHasDeletedAt(initialData)) {
              runInAction(() => {
                this.applyDeletedSessionBlock(initialData as FirestoreSession);
              });
              this.previousSessionId = this.currentSessionId;
            } else {
              runInAction(() => {
                this.currentSessionError = undefined;
                this.currentSessionDeletedBlock = undefined;
              });
              updateCurrentSession(session);
              const loadedSession = this.sessions[this.currentSessionId];
              if (loadedSession) {
                loadedSession.ensureReactionActive();
              }
              this.previousSessionId = this.currentSessionId;
            }
          }
        } catch (e) {
          console.error('[SessionsStore] Error loading session:', e);
          // Debug: surface error details for permission issues
          // eslint-disable-next-line no-console
          console.error('[SessionsStore] Session read failed', {
            sessionId: this.currentSessionId,
            isGuest: user?.isGuest,
            code: (e as { code?: string }).code,
            message: (e as { message?: string }).message,
          });

          // Check if this might be a guest user - check Firebase Auth directly if store isn't ready
          let isGuestUser = false;
          let guestSessionId: string | undefined;

          if (user?.isGuest && user?.guestSessionId === this.currentSessionId) {
            isGuestUser = true;
            guestSessionId = user.guestSessionId;
          } else {
            // Auth store might not be ready yet - check Firebase Auth directly
            try {
              const { auth } = await import('firebase-modules');
              const firebaseUser = auth.currentUser;
              if (firebaseUser) {
                const tokenResult = await firebaseUser.getIdTokenResult();
                if (tokenResult.claims.guest === true) {
                  isGuestUser = true;
                  guestSessionId = tokenResult.claims.sessionId as string;
                }
              }
            } catch (authCheckError) {
              console.warn(
                '[SessionsStore] Could not check Firebase Auth for guest user:',
                authCheckError,
              );
            }
          }

          // For guest users, check if they have access via custom claims before showing error
          if (isGuestUser && guestSessionId === this.currentSessionId) {
            // Guest user with valid session access - try to load session anyway
            // The Firestore rules should allow this
            try {
              // Try to set up listener - Firestore rules should allow guest access
              const guestSession = await currentSessionRef.get();
              if (guestSession.exists) {
                updateCurrentSession(guestSession);
                this.stopListeningToCurrentSession =
                  currentSessionRef.onSnapshot(updateCurrentSession);
                const currentSession = this.sessions[this.currentSessionId];
                if (currentSession) {
                  currentSession.ensureReactionActive();
                }
                this.previousSessionId = this.currentSessionId;
                this.currentSessionError = undefined;
                this.currentSessionDeletedBlock = undefined;
              } else {
                console.error('[SessionsStore] Guest session document does not exist');
                throw new Error('Session does not exist');
              }
            } catch (guestError) {
              // If guest access still fails, it might be expired or invalid
              console.error('[SessionsStore] Guest user session access failed:', guestError);
              this.currentSessionError = 403;
              // Try to get owner info for error message
              try {
                const ownerData = await getSessionSharingInfo({
                  sessionId: this.currentSessionId,
                });
                this.setCurrentSessionOwner(ownerData.data);
              } catch (ownerError) {
                // Silently fail - owner info is optional
                console.warn('[SessionsStore] Failed to get owner info:', ownerError);
              }
              this.previousSessionId = this.currentSessionId;
            }
          } else {
            // Non-guest user or guest without matching session - show access denied
            this.currentSessionError = 403;
            try {
              const ownerData = await getSessionSharingInfo({
                sessionId: this.currentSessionId,
              });
              this.setCurrentSessionOwner(ownerData.data);
            } catch (ownerError) {
              // Silently fail - owner info is optional
              console.warn('[SessionsStore] Failed to get owner info:', ownerError);
            }
            // Track the current session ID even on error
            this.previousSessionId = this.currentSessionId;
          }
        }

        void currentSessionRef
          .get({ source: 'server' })
          .then((snapshot) => {
            if (this.currentSessionId !== sessionId) {
              return;
            }
            if (snapshot.exists) {
              updateCurrentSession(snapshot);
            }
          })
          .catch((error) => {
            console.error('[SessionsStore] Background session refresh failed:', error);
          });
      } else {
        // If there's no current session, clear the previous session ID
        this.previousSessionId = null;
      }
    } finally {
      if (shouldFinishStartupTracing) {
        finishStartupTracing();
      }
    }
  };

  watchHiddenSessionsForUser = () => {
    this.stopListeningToUserHiddenSessions();
    const {
      authStore: { user },
    } = this.rootStore;

    if (!user || user.isGuest) {
      runInAction(() => {
        this.hiddenSessionIds = [];
      });
      return;
    }

    const ref = db.users(user.uid);
    this.stopListeningToUserHiddenSessions = ref.onSnapshot(
      (snap) => {
        const hidden = snap.data()?.hiddenSessions;
        const newIds = Array.isArray(hidden) ? hidden.slice() : [];
        const prev = this.hiddenSessionIds;
        if (newIds.length === prev.length && newIds.every((id, i) => id === prev[i])) return;
        runInAction(() => {
          this.hiddenSessionIds = newIds;
        });
      },
      (err) => {
        console.error('[SessionsStore] hiddenSessions listener error:', err);
      },
    );
  };

  watchSessions = () => {
    const {
      authStore: { user },
    } = this.rootStore;
    this.stopListeningToSessions();
    this.stopListeningToCurrentSession();
    this.resetSessions();

    // Guest users don't own sessions - they access via custom claims
    // Skip this query for guests to avoid "Missing or insufficient permissions" error
    if (user && !user.isGuest) {
      const userSessionsRef = db.sessions
        .where('userId', '==', user.uid)
        .orderBy('createdAt', 'desc');
      this.stopListeningToSessions = userSessionsRef.onSnapshot(this.updateSessions);
    }
  };

  watchSharedSessions = () => {
    const {
      authStore: { user },
    } = this.rootStore;

    // Always stop previous listener before starting a new one
    this.stopListeningToSharedSessions();
    this.stopListeningToOrgIdSharedSessions();
    this.stopListeningToDirectShareSessions();

    if (!user) {
      runInAction(() => {
        this.sharedSessions = {};
      });
      this.lastSharedSessionsLog = null;
      return;
    }

    // Guest users only access their specific session via custom claims
    // They don't have permission documents or organization membership
    // Skip all shared session queries to avoid "Missing or insufficient permissions" errors
    if (user.isGuest) {
      runInAction(() => {
        this.sharedSessions = {};
      });
      return;
    }

    const { organizationId } = user;

    // If org membership hasn't been resolved yet, don't spam logs.
    // AuthStore sets `user` first, then later fills in org fields asynchronously.
    if (!organizationId && user.hasOrganization === undefined) {
      return;
    }

    // Load organization-shared sessions
    // Strategy: Query permissions subcollection instead of sessions collection
    // This avoids Firestore rule evaluation issues when querying by sharing.organizationId
    // shareSessionWithOrg creates permission documents for each member, so we can query those
    const mergeSharedSessions = async (
      sessionDocs: Array<{ id: string; data: FirestoreSession }>,
    ) => {
      const newSharedSessions: Record<string, Session> = {};

      // Create a Set of direct share session IDs for efficient lookup
      const directShareSessionIds = new Set(lastDirectShareSessions.map((s) => s.id));

      // Pre-load all team memberships for teams that any session is shared with
      // Only if user has an organization
      const teamIdsToCheck = new Set<string>();
      const userTeamIds = new Set<string>();

      if (organizationId) {
        sessionDocs.forEach(({ data }) => {
          const sessionTeamIds = data.sharing?.teamIds;
          if (sessionTeamIds && sessionTeamIds.length > 0) {
            sessionTeamIds.forEach((teamId) => teamIdsToCheck.add(teamId));
          }
        });

        // Load team members for all relevant teams in parallel
        if (teamIdsToCheck.size > 0) {
          await Promise.all(
            Array.from(teamIdsToCheck).map((teamId) =>
              this.rootStore.uiTeamsStore.loadTeamMembers(organizationId, teamId),
            ),
          );

          // Get user's team memberships once
          for (const teamId of teamIdsToCheck) {
            const teamMembers = this.rootStore.uiTeamsStore.getTeamMembersByTeam(
              organizationId,
              teamId,
            );
            if (teamMembers.some((member) => member.userId === user.uid)) {
              userTeamIds.add(teamId);
            }
          }
        }
      }

      sessionDocs.forEach(({ id, data }) => {
        // Validate id is a string
        if (typeof id !== 'string' || !id) {
          // eslint-disable-next-line no-console
          console.warn('[SessionsStore] Invalid session ID:', id);
          return;
        }

        // Skip sessions owned by the current user (already in orderedSessions)
        if (data.userId === user.uid) return;

        // IMPORTANT: Skip sessions that are explicitly set to private mode IF they are org-shared
        // Private sessions should not be visible to organization members unless they have direct access
        // Direct shares work independently and are visible even if the session is private
        //
        // The key logic:
        // 1. If a session is private AND it came from org sharing (lastBySharing or lastByOrgId), skip it
        // 2. If a session is private AND it came from direct sharing (lastDirectShareSessions), show it
        // 3. We can't reliably distinguish the source after merging, so we check:
        //    - If private AND matches org criteria AND NOT in direct share list, skip it
        //    - If private AND in direct share list, show it (direct shares override privacy)
        //
        // To determine if it's a direct share, we check if the session ID is in the direct share set
        const isDirectShare = directShareSessionIds.has(id);

        // IMPORTANT: Skip private sessions owned by other users unless they're direct shares
        // Duplicated sessions are set to private mode and should only appear for their owner
        // They should not appear in shared sessions unless explicitly shared via direct share
        if (
          data.userId !== user.uid &&
          data.publicAccessLevel === SessionPermissions.PRIVATE &&
          (!data.sharing || data.sharing.mode === 'private')
        ) {
          // Only show if it's a direct share (has permission document)
          if (!isDirectShare) {
            // This is a private session owned by someone else - skip it unless it's a direct share
            return;
          }
        }

        if (data.sharing?.mode === 'private') {
          // If it's a direct share, show it (direct shares work even for private sessions)
          if (isDirectShare) {
            // Continue processing - this is a direct share, so it should be visible
          } else {
            // Not a direct share - check if it matches org-shared criteria
            // If it matches org-shared criteria but is private and NOT a direct share, skip it
            if (this.isOrgSharedSession(data, organizationId, false)) {
              return;
            }
            // If it doesn't match org criteria, it might be a private session with no sharing
            // In that case, also skip it (only direct shares can access private sessions)
            return;
          }
        }

        // Check if this is an org-shared session
        const isOrgSharedViaSharing =
          organizationId &&
          data.sharing?.mode === 'org' &&
          data.sharing?.organizationId === organizationId;

        // Include if org-shared via legacy/top-level fields
        const isOrgSharedViaOrgId =
          organizationId &&
          data.organizationId === organizationId &&
          (data.publicAccessLevel === SessionPermissions.VIEW ||
            data.publicAccessLevel === SessionPermissions.EDIT) &&
          (data.sharing?.mode === undefined || data.sharing?.mode === 'org'); // Only if not explicitly private

        // If session is org-shared, apply org/team filtering
        if (isOrgSharedViaSharing || isOrgSharedViaOrgId) {
          // If session is shared with specific teams, check if user is a member of any of those teams
          const teamIds = data.sharing?.teamIds;
          if (teamIds && teamIds.length > 0) {
            // Session is shared with specific teams, not entire org
            // Check if current user is a member of any of these teams
            const isUserInAnyTeam = teamIds.some((teamId) => userTeamIds.has(teamId));

            // Skip this session if user is not a member of any of the shared teams
            if (!isUserInAnyTeam) return;
          }
          // If no teamIds or user is in a team, include this org-shared session
        } else {
          // This is not an org-shared session - it's a direct share
          // Direct shares are included if they're in the sessionDocs array
          // (they're already filtered by permission document existence in the query)
          // Direct shares work independently of sharing mode - they're visible even if the session is private
          // So include them regardless of sharing mode or org membership
        }

        // Use the EXACT same pattern as owner sessions - no normalization
        // Just create the Session the same way updateSessions does it
        const existingSession = this.sharedSessions[id];
        if (existingSession) {
          // Use updateData action instead of direct assignment to avoid MobX strict-mode warning
          existingSession.updateData(data);
          newSharedSessions[id] = existingSession;
        } else {
          try {
            // Create Session exactly like owner sessions do in updateSessions
            newSharedSessions[id] = new Session(id, data, this.rootStore);
          } catch (error: any) {
            // eslint-disable-next-line no-console
            console.error(`[SessionsStore] Error creating shared Session for ${id}:`, error);
            // eslint-disable-next-line no-console
            console.error('[SessionsStore] Error details:', {
              message: error?.message,
              stack: error?.stack,
            });
            // Log the actual data to see what's wrong
            // eslint-disable-next-line no-console
            console.error('[SessionsStore] Session data types:', {
              cardIds: Array.isArray(data.cardIds) ? 'array' : typeof data.cardIds,
              moduleIds: Array.isArray(data.moduleIds) ? 'array' : typeof data.moduleIds,
              sourceIds: Array.isArray(data.sourceIds) ? 'array' : typeof data.sourceIds,
              cardTitleFormula: Array.isArray(data.cardTitleFormula)
                ? 'array'
                : typeof data.cardTitleFormula,
              filenameFormula: Array.isArray(data.filenameFormula)
                ? 'array'
                : typeof data.filenameFormula,
              modules: typeof data.modules,
              visibility: typeof data.visibility,
              name: typeof data.name,
              userId: typeof data.userId,
            });
            // Skip this session to prevent breaking the entire list
          }
        }
      });

      runInAction(() => {
        this.sharedSessions = newSharedSessions;
        // Track for deduplication without logging
        const count = Object.keys(newSharedSessions).length;
        if (
          !this.lastSharedSessionsLog ||
          this.lastSharedSessionsLog.organizationId !== (organizationId || null) ||
          this.lastSharedSessionsLog.count !== count
        ) {
          this.lastSharedSessionsLog = { organizationId: organizationId || '', count };
        }
      });
    };

    // Keep last snapshots so we can merge across all listeners (Firestore has no OR queries)
    let lastBySharing: Array<{ id: string; data: FirestoreSession }> = [];
    let lastByOrgId: Array<{ id: string; data: FirestoreSession }> = [];
    let lastDirectShareSessions: Array<{ id: string; data: FirestoreSession }> = [];

    const recompute = async () => {
      const mergedMap = new Map<string, FirestoreSession>();
      // Add org-shared sessions
      lastBySharing.forEach((s) => mergedMap.set(s.id, s.data));
      lastByOrgId.forEach((s) => mergedMap.set(s.id, s.data));
      // Add direct share sessions (sessions where user has permission document)
      lastDirectShareSessions.forEach((s) => mergedMap.set(s.id, s.data));
      const merged = Array.from(mergedMap.entries()).map(([id, data]) => ({ id, data }));
      await mergeSharedSessions(merged);
    };

    // Load direct share sessions (sessions where user has permission document)
    // This works regardless of organization membership
    // Query collectionGroup by userId field (permission documents store userId as a field)
    // Note: Filtering by status client-side until the index with status is fully built
    const directSharePermissionsRef = firestoreDb
      .collectionGroup('permissions')
      .where('userId', '==', user.uid);

    this.stopListeningToDirectShareSessions = directSharePermissionsRef.onSnapshot(
      async (snapshot) => {
        // Track session IDs from permission documents
        // Use docChanges() to detect when permissions are removed (for immediate updates)
        const sessionIds = new Set<string>();

        // Process all current docs to build the complete set of active permissions
        // Only include permissions that were granted as direct shares (not via org/team membership)
        // Valid direct share grantedVia values: 'direct', 'session_share', 'manual'
        const directShareGrantedViaValues = ['direct', 'session_share', 'manual'];

        snapshot.docs.forEach((permissionDoc) => {
          const permissionData = permissionDoc.data();
          // Only include active permissions (filter client-side)
          if (permissionData?.status !== true) {
            return;
          }
          // Only include permissions that are actual direct shares
          // Permissions granted via 'organization', 'team', 'domain_auto', etc. should be
          // accessed via org-shared sessions, not as direct shares
          const grantedVia = permissionData?.grantedVia;
          if (!grantedVia || !directShareGrantedViaValues.includes(grantedVia)) {
            return;
          }
          // Extract session ID from path: sessions/{sessionId}/permissions/{userId}
          const pathParts = permissionDoc.ref.path.split('/');
          if (pathParts.length >= 4 && pathParts[0] === 'sessions') {
            const sessionId = pathParts[1];
            sessionIds.add(sessionId);
          }
        });

        // Process docChanges() to handle removals immediately
        // This ensures sessions are removed as soon as permissions are deleted
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'removed') {
            const permissionDoc = change.doc;
            // Extract session ID from path: sessions/{sessionId}/permissions/{userId}
            const pathParts = permissionDoc.ref.path.split('/');
            if (pathParts.length >= 4 && pathParts[0] === 'sessions') {
              const sessionId = pathParts[1];
              // Remove session ID immediately when permission is deleted
              sessionIds.delete(sessionId);
            }
          }
        });

        // Load session documents for direct shares
        if (sessionIds.size > 0) {
          const sessionPromises = Array.from(sessionIds).map(async (sessionId) => {
            try {
              const sessionDoc = await db.sessions.doc(sessionId).get();
              // Check exists as property (not function) - matches the pattern used elsewhere in this file
              if (sessionDoc.exists !== false) {
                const data = sessionDoc.data();
                if (data && data.userId !== user.uid) {
                  // Skip sessions owned by current user (already in orderedSessions)
                  return { id: sessionId, data };
                }
              }
              return null;
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error(
                `[SessionsStore] Error loading direct share session ${sessionId}:`,
                error,
              );
              return null;
            }
          });

          const sessionResults = await Promise.all(sessionPromises);
          lastDirectShareSessions = sessionResults.filter(
            (s): s is { id: string; data: FirestoreSession } => s !== null,
          );
          // Update observable array of direct share session IDs
          runInAction(() => {
            this.directShareSessionIds = lastDirectShareSessions.map((s) => s.id);
          });

          // Fetch organization names for external shares
          const orgIdsToFetch = new Set<string>();
          lastDirectShareSessions.forEach((s) => {
            const orgId = s.data.sharing?.organizationId || s.data.organizationId;
            if (orgId && !this.organizationNames[orgId]) {
              orgIdsToFetch.add(orgId);
            }
          });
          if (orgIdsToFetch.size > 0) {
            await Promise.all(
              Array.from(orgIdsToFetch).map(async (orgId) => {
                try {
                  const orgDoc = await db.organizations.doc(orgId).get();
                  const orgData = orgDoc.data();
                  if (orgData?.name) {
                    runInAction(() => {
                      this.organizationNames[orgId] = orgData.name;
                    });
                  }
                } catch (error) {
                  // eslint-disable-next-line no-console
                  console.warn(`[SessionsStore] Failed to fetch org name for ${orgId}:`, error);
                }
              }),
            );
          }
        } else {
          lastDirectShareSessions = [];
          // Clear observable array
          runInAction(() => {
            this.directShareSessionIds = [];
          });
        }

        // Recompute to merge org and direct shares
        await recompute();
      },
      async (error: any) => {
        // eslint-disable-next-line no-console
        console.error('[SessionsStore] Error loading direct share sessions:', error);
        // eslint-disable-next-line no-console
        console.error('[SessionsStore] Error details:', {
          code: error?.code,
          message: error?.message,
          stack: error?.stack,
        });
        lastDirectShareSessions = [];
        // Clear observable array on error
        runInAction(() => {
          this.directShareSessionIds = [];
        });
        await recompute();
      },
    );

    // If user doesn't have an organization, still load direct shares but skip org shares
    if (!organizationId) {
      // Direct share listener already set up above, so just return
      // The recompute function will only process direct shares since lastBySharing/lastByOrgId will be empty
      return;
    }

    // Query sessions directly by sharing.organizationId
    // The Firestore rule will check organization membership, so new members automatically
    // see org-shared sessions without needing permission documents created for them.
    // This is the correct approach: org-shared sessions should be visible to all org members.
    // IMPORTANT: Use db.sessions (with converter) not firestoreDb.collection() to match owner sessions
    // Only create these queries if organizationId is defined (guests don't have organizations)
    const sessionsBySharingOrgIdRef = db.sessions
      .where('sharing.organizationId', '==', organizationId)
      .where('sharing.mode', '==', 'org');

    const sessionsByOrgIdRef = db.sessions.where('organizationId', '==', organizationId);

    this.stopListeningToSharedSessions = sessionsBySharingOrgIdRef.onSnapshot(
      async (snapshot) => {
        lastBySharing = snapshot.docs.map((d) => ({
          id: d.id,
          data: d.data() as FirestoreSession,
        }));
        await recompute();
      },
      async (error) => {
        // eslint-disable-next-line no-console
        console.error(
          '[SessionsStore] Error loading shared sessions (sharing.organizationId):',
          error,
        );
        lastBySharing = [];
        await recompute();
      },
    );

    this.stopListeningToOrgIdSharedSessions = sessionsByOrgIdRef.onSnapshot(
      async (snapshot) => {
        // Filter out private sessions at the source - they should only be visible via direct shares
        lastByOrgId = snapshot.docs
          .map((d) => ({ id: d.id, data: d.data() as FirestoreSession }))
          .filter(({ data }) => {
            // Exclude private sessions from org-based queries
            // Private sessions should only be visible via direct shares (lastDirectShareSessions)
            return data.sharing?.mode !== 'private';
          });
        await recompute();
      },
      async (error) => {
        // eslint-disable-next-line no-console
        console.error('[SessionsStore] Error loading shared sessions (organizationId):', error);
        lastByOrgId = [];
        await recompute();
      },
    );
  };

  /**
   * True if a session is in the user's org scope (same predicate as the Projects "Org" filter).
   * Used for org-owner/admin visibility in the Deleted projects list.
   */
  sessionMatchesOrgScope(session: Session, organizationId: string | undefined): boolean {
    if (!organizationId) return false;
    return this.isOrgSharedSession(session.data, organizationId, true);
  }

  /**
   * Helper method to check if a session is shared with an organization
   * @param sessionData - The session data to check
   * @param organizationId - The organization ID to check against
   * @param requirePublicAccess - If true, requires stricter checks:
   *   - For new format: requires sharing.mode === 'org'
   *   - For legacy format: requires VIEW/EDIT public access level and correct sharing mode
   * @returns true if the session is org-shared
   */
  private isOrgSharedSession(
    sessionData: FirestoreSession,
    organizationId: string | undefined,
    requirePublicAccess = false,
  ): boolean {
    if (!organizationId) {
      return false;
    }

    // Check if session is org-shared via sharing.organizationId (new format)
    let isOrgSharedViaSharing = false;
    if (requirePublicAccess) {
      // Stricter check: requires mode === 'org'
      isOrgSharedViaSharing =
        sessionData.sharing?.mode === 'org' &&
        sessionData.sharing?.organizationId === organizationId;
    } else {
      // Simple check: just organizationId match (used for filtering private sessions)
      isOrgSharedViaSharing = sessionData.sharing?.organizationId === organizationId;
    }

    // Check if session is org-shared via legacy/top-level organizationId
    const isOrgSharedViaOrgId = sessionData.organizationId === organizationId;

    // If requirePublicAccess is true, also check public access level for legacy org sharing
    if (requirePublicAccess && isOrgSharedViaOrgId) {
      const hasPublicAccess =
        sessionData.publicAccessLevel === SessionPermissions.VIEW ||
        sessionData.publicAccessLevel === SessionPermissions.EDIT;
      const hasCorrectSharingMode =
        sessionData.sharing?.mode === undefined || sessionData.sharing?.mode === 'org';
      return isOrgSharedViaSharing || (hasPublicAccess && hasCorrectSharingMode);
    }

    return isOrgSharedViaSharing || isOrgSharedViaOrgId;
  }

  /**
   * Get the share provenance for a session (Solo, Team, Org, or External)
   * Used for displaying share-status badges on project cards
   */
  getShareProvenance(sessionId: string): {
    type: 'Solo' | 'Team' | 'Org' | 'External';
    label: string;
  } {
    const user = this.rootStore.authStore.user;
    const session = this.sessions[sessionId] || this.sharedSessions[sessionId];

    if (!session || !user) {
      return { type: 'Solo', label: 'Solo' };
    }

    // Solo: Session is owned by the current user
    if (session.data.userId === user.uid) {
      return { type: 'Solo', label: 'Solo' };
    }

    // External: Session is a direct share from outside org/team
    if (this.directShareSessionIds.includes(sessionId)) {
      const orgId = session.data.sharing?.organizationId || session.data.organizationId;
      const orgName = orgId ? this.organizationNames[orgId] : undefined;
      return { type: 'External', label: orgName || 'External' };
    }

    // Org: Session is shared via organization
    const organizationId = user.organizationId;
    if (organizationId && this.isOrgSharedSession(session.data, organizationId, true)) {
      return { type: 'Org', label: 'Org' };
    }

    // Team: Any other shared session (not owned, not external, not org)
    return { type: 'Team', label: 'Team' };
  }

  get liveSessions() {
    return this.orderedSessions.filter((s) => !s.data.deletedAt);
  }

  get deletedSessions() {
    return this.orderedSessions.filter((s) => !!s.data.deletedAt);
  }

  get activeSessions() {
    return this.liveSessions.filter((s) => !s.data.isArchived);
  }

  get archivedSessions() {
    return this.liveSessions.filter((s) => s.data.isArchived);
  }

  /** All sessions (owned + shared) without any filter applied */
  get allSessions(): Session[] {
    return [...this.orderedSessions, ...Object.values(this.sharedSessions)];
  }

  get filteredSessions(): Session[] {
    const user = this.rootStore.authStore.user;
    const organizationId = user?.organizationId;

    switch (this.currentFilter) {
      case SessionFilter.MINE:
        return this.allSessions.filter((s) => s.data.userId === user?.uid);
      case SessionFilter.SHARED:
        // Team filter: sessions not owned by user AND not external shares
        // This is reserved for sessions shared within the user's organization/team
        return this.allSessions.filter(
          (s) => s.data.userId !== user?.uid && !this.directShareSessionIds.includes(s.id),
        );
      case SessionFilter.ORG:
        if (!organizationId) {
          return [];
        }
        return this.allSessions.filter((s) =>
          this.isOrgSharedSession(s.data, organizationId, true),
        );
      case SessionFilter.EXTERNAL:
        // External shares are sessions shared directly from outside the user's org/team
        // These are tracked in directShareSessionIds and are NOT owned by the user
        return this.allSessions.filter(
          (s) => s.data.userId !== user?.uid && this.directShareSessionIds.includes(s.id),
        );
      case SessionFilter.ALL:
      default:
        return this.allSessions;
    }
  }

  get filteredActiveSessions(): Session[] {
    return this.filteredSessions.filter((s) => !s.data.deletedAt && !s.data.isArchived);
  }

  get filteredArchivedSessions(): Session[] {
    return this.filteredSessions.filter((s) => !s.data.deletedAt && s.data.isArchived);
  }

  setCurrentFilter = (filter: SessionFilter) => {
    this.currentFilter = filter;
  };

  get currentSessionId(): string {
    const { routerState } = this.rootStore.routerStore;
    const sessionId = routerState?.params?.sessionId;
    if (typeof sessionId === 'string' && sessionId) {
      return sessionId;
    }
    return '';
  }

  get currentSession(): Session | null {
    if (this.currentSessionError === 'deleted') {
      return null;
    }
    const session =
      this.sessions[this.currentSessionId] ?? this.sharedSessions[this.currentSessionId];
    if (!session) {
      return null;
    }
    if (SessionsStore.sessionHasDeletedAt(session.data)) {
      return null;
    }
    return session;
  }

  archiveSession = (sessionId: string) => {
    trackAction('session_archived');
    const sessionRef = db.sessions.doc(sessionId);
    sessionRef.set(
      {
        isArchived: true,
      },
      {
        merge: true,
      },
    );
  };

  unarchiveSession = (sessionId: string) => {
    trackAction('session_unarchived');
    const sessionRef = db.sessions.doc(sessionId);
    sessionRef.set(
      {
        isArchived: false,
      },
      {
        merge: true,
      },
    );
  };

  deleteSession = async (sessionId: string) => {
    trackAction('session_deleted');
    const callable = fn.httpsCallable('adminSoftDeleteSession');
    await callable({ sessionId });
  };

  restoreSoftDeletedSession = async (sessionId: string) => {
    const callable = fn.httpsCallable('adminRestoreSession');
    await callable({ sessionId });
  };

  markSessionAsOpened = (sessionId: string) => {
    trackAction('session_opened');
    const sessionRef = db.sessions.doc(sessionId);
    sessionRef.set(
      { lastOpenedAt: FieldValue.serverTimestamp() as unknown as Date },
      { merge: true },
    );
  };

  hideSession = (sessionId: string) => {
    const { user } = this.rootStore.authStore;
    if (!user?.uid || user.isGuest) return;
    const MAX_HIDDEN = 2000;
    if (this.hiddenSessionIds.length >= MAX_HIDDEN) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `You can hide up to ${MAX_HIDDEN} projects. Unhide some first.`,
      });
      return;
    }
    db.users(user.uid).set(
      { hiddenSessions: FieldValue.arrayUnion(sessionId) as unknown as string[] },
      { merge: true },
    );
  };

  unhideSession = (sessionId: string) => {
    const { user } = this.rootStore.authStore;
    if (!user?.uid || user.isGuest) return;
    db.users(user.uid).set(
      { hiddenSessions: FieldValue.arrayRemove(sessionId) as unknown as string[] },
      { merge: true },
    );
  };

  requestSessionDeletion = async (sessionId: string, reason?: string) => {
    const trimmed = reason?.trim().slice(0, 500) ?? '';
    await fn.httpsCallable('requestSessionDeletion')({ sessionId, reason: trimmed });
  };

  getSessionOwnershipTransferCandidates = async (
    sessionId: string,
  ): Promise<SessionOwnershipTransferCandidate[]> => {
    const callable = fn.httpsCallable('getSessionOwnershipTransferCandidates');
    const result = await callable({ sessionId });
    const { candidates } = result.data as { candidates: SessionOwnershipTransferCandidate[] };
    return candidates ?? [];
  };

  transferSessionOwnership = async (
    sessionId: string,
    newOwnerId: string,
  ): Promise<{ success: boolean; message: string; newOwnerEmail: string | null }> => {
    const callable = fn.httpsCallable('transferSessionOwnership');
    const result = await callable({ sessionId, newOwnerId });
    return result.data as { success: boolean; message: string; newOwnerEmail: string | null };
  };

  /**
   * Normalize Firestore session data to ensure all fields are in the expected format.
   * This prevents errors when Session constructor accesses fields that might be null/undefined
   * or in unexpected formats (e.g., calling indexOf on a non-array).
   * @internal - Currently unused but kept for potential future use
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // @ts-expect-error - Unused method kept for potential future use
  private _normalizeSessionData(data: FirestoreSession): FirestoreSession {
    return {
      ...data,
      // Ensure all array fields are arrays
      cardIds: Array.isArray(data.cardIds) ? data.cardIds : [],
      moduleIds: Array.isArray(data.moduleIds) ? data.moduleIds : [],
      sourceIds: Array.isArray(data.sourceIds) ? data.sourceIds : [],
      cardTitleFormula: Array.isArray(data.cardTitleFormula) ? data.cardTitleFormula : [],
      filenameFormula: Array.isArray(data.filenameFormula) ? data.filenameFormula : [],
      // Ensure object fields are objects
      modules:
        data.modules && typeof data.modules === 'object' && !Array.isArray(data.modules)
          ? data.modules
          : {},
      visibility:
        data.visibility && typeof data.visibility === 'object' && !Array.isArray(data.visibility)
          ? {
              ...data.visibility,
              hiddenModuleIds: Array.isArray(data.visibility.hiddenModuleIds)
                ? data.visibility.hiddenModuleIds
                : [],
              hideFilename:
                typeof data.visibility.hideFilename === 'boolean'
                  ? data.visibility.hideFilename
                  : false,
            }
          : {
              hiddenModuleIds: [],
              hideFilename: false,
            },
      moduleDisplaySettings:
        data.moduleDisplaySettings &&
        typeof data.moduleDisplaySettings === 'object' &&
        !Array.isArray(data.moduleDisplaySettings)
          ? data.moduleDisplaySettings
          : {},
      // Ensure string fields are strings
      name: typeof data.name === 'string' ? data.name : '',
      userId: typeof data.userId === 'string' ? data.userId : '',
    };
  }

  updateSessions(snapshot: ft.firestore.QuerySnapshot<FirestoreSession>) {
    snapshot.docChanges().forEach((change: ft.firestore.DocumentChange<FirestoreSession>) => {
      const changeType = change.type;
      const changeDoc = change.doc;
      if (changeType === 'added') {
        // Keep owner sessions unchanged - use Firestore data as-is
        this.sessions[changeDoc.id] = new Session(changeDoc.id, changeDoc.data(), this.rootStore);
      } else if (change.type === 'modified') {
        // Keep owner sessions unchanged - use Firestore data as-is
        this.sessions[changeDoc.id].updateData(changeDoc.data());
      } else {
        // Dispose of the session before removing it to prevent memory leaks
        const session = this.sessions[changeDoc.id];
        if (session) {
          session.dispose();
        }
        delete this.sessions[changeDoc.id];
      }
    });

    this.orderedSessions = snapshot.docs
      .map((doc: ft.firestore.QueryDocumentSnapshot<FirestoreSession>) => this.sessions[doc.id])
      .filter((session: Session | undefined): session is Session => session !== undefined);
  }

  resetSessions() {
    this.orderedSessions.splice(0, this.orderedSessions.length);
    // Dispose of all sessions before removing them to prevent memory leaks
    for (const key of Object.keys(this.sessions)) {
      const session = this.sessions[key];
      if (session) {
        session.dispose();
      }
      delete this.sessions[key];
    }
  }

  // Back-end operations
  createNewSession = async ({
    name,
    projectType,
  }: { name?: string; projectType?: string } = {}) => {
    if (!this.rootStore.authStore.user) {
      return;
    }
    trackAction('session_created', projectType ? 'quickstart' : undefined);
    const newSessionRef = db.sessions.doc();
    const sessionData: any = {
      createdAt: FieldValue.serverTimestamp() as unknown as Date,
      modules: {},
      moduleIds: [],
      name: name || 'Untitled Session',
      cardIds: [],
      sourceIds: [],
      userId: this.rootStore.authStore.user.uid,
      createdBy: {
        userId: this.rootStore.authStore.user.uid,
        email: this.rootStore.authStore.user.email,
      },
      filenameSettings: {
        cardNumberWidth: 3,
        spaceSeparator: '-',
        fieldSeparator: '_',
        specialCharacterReplacement: '',
        disableFilenameCleanup: false,
        disableSpecialCharacterCleanup: true,
        filenameCaps: true,
        trimSpaces: false,
        trimSpecialCharacters: false,
      },
      visibility: {
        hiddenModuleIds: [],
        hideFilename: true, // Hide filename by default for new projects
      },
      cardTitleFormula: [],
      filenameFormula: [],
      automaticModuleCreation: true,
      automaticFilenameVersioning: true,
      automaticCardTitleSuffix: true,
      doubleClickBucketsToOpenLibrary: true,
      publicAccessLevel: SessionPermissions.PRIVATE,
      moduleDisplaySettings: {},
    };
    if (projectType) {
      sessionData.projectType = projectType;
    }
    const userOrganizationId = this.rootStore.authStore.user?.organizationId;
    if (userOrganizationId) {
      sessionData.organizationId = userOrganizationId;
    }
    await newSessionRef.set(sessionData);

    // Create default layout preset for new sessions
    try {
      const defaultPresetId = ulid();
      const defaultPreset: LayoutPreset = {
        name: 'Default Layout',
        moduleIds: [],
        visibility: {
          hiddenModuleIds: [],
          hideFilename: true,
        },
        moduleDisplaySettings: {},
        connectionDisplaySettings: {},
        createdAt: new Date(),
      };

      await newSessionRef.update({
        [`layoutPresets.${defaultPresetId}`]: defaultPreset,
      });
    } catch (error: any) {
      console.error('Failed to create default preset:', error);
      // Don't fail the entire operation if preset creation fails
    }

    await this.rootStore.routerStore.goTo(RouteNames.SESSION_QUEUE, {
      params: { sessionId: newSessionRef.id },
    });
    return newSessionRef.id;
  };

  createNewSessionWithSources = async ({
    name,
    projectType,
    visibleSources = [],
    sources,
    preConnectFirstCard = false,
    cardCount = 1,
    steps,
  }: {
    name: string;
    projectType?: string;
    visibleSources?: string[];
    sources: Array<{
      name: string;
      source: import('@creative-kit/shared').FirestoreSource;
      rows: import('@creative-kit/shared').RowData[];
      pdfFiles?: File[];
      imageFiles?: File[];
    }>;
    /** If true, pre-connect the first card to the first row of the first source */
    preConnectFirstCard?: boolean;
    /** Number of starter cards to create (default: 1) */
    cardCount?: number;
    /** Workflow steps to seed the session with; starter cards land in steps[0]. */
    steps?: import('@creative-kit/shared').SessionStep[];
  }): Promise<{ sessionId?: string; errors?: string[] }> => {
    if (!this.rootStore.authStore.user) {
      return { errors: ['User not authenticated'] };
    }
    trackAction('session_created', 'quickstart');

    const errors: string[] = [];

    // Create session document
    const newSessionRef = db.sessions.doc();
    const sessionData: any = {
      createdAt: FieldValue.serverTimestamp() as unknown as Date,
      modules: {},
      moduleIds: [],
      name,
      cardIds: [],
      sourceIds: [],
      userId: this.rootStore.authStore.user.uid,
      createdBy: {
        userId: this.rootStore.authStore.user.uid,
        email: this.rootStore.authStore.user.email,
      },
      filenameSettings: {
        cardNumberWidth: 3,
        spaceSeparator: '-',
        fieldSeparator: '_',
        specialCharacterReplacement: '',
        disableFilenameCleanup: false,
        disableSpecialCharacterCleanup: true,
        filenameCaps: true,
        trimSpaces: false,
        trimSpecialCharacters: false,
      },
      visibility: {
        hiddenModuleIds: [],
        hideFilename: true, // Hide filename by default for new projects
      },
      cardTitleFormula: [],
      filenameFormula: [],
      automaticModuleCreation: true,
      automaticFilenameVersioning: true,
      automaticCardTitleSuffix: true,
      doubleClickBucketsToOpenLibrary: true,
      publicAccessLevel: SessionPermissions.PRIVATE,
      moduleDisplaySettings: {},
    };
    if (projectType) {
      sessionData.projectType = projectType;
    }
    if (steps && steps.length > 0) {
      sessionData.steps = steps;
    }

    const userOrganizationId = this.rootStore.authStore.user?.organizationId;
    if (userOrganizationId) {
      sessionData.organizationId = userOrganizationId;
    }

    try {
      await newSessionRef.set(sessionData);
    } catch (error: any) {
      return { errors: [`Failed to create session: ${error.message}`] };
    }

    const createdSourceIds: string[] = [];
    const createdModuleIds: string[] = [];
    const ulidFactory = monotonicFactory();
    // Map source index to source ID to handle cases where some sources fail to create
    const sourceIdByIndex: (string | undefined)[] = new Array(sources.length);
    // Track row IDs per source for connecting cards
    const rowIdsBySourceIndex: string[][] = new Array(sources.length).fill(null).map(() => []);

    // Create sources and rows
    for (let i = 0; i < sources.length; i++) {
      const sourceConfig = sources[i];
      try {
        const sourceRef = db.sessionSources(newSessionRef.id).doc();
        const sourceId = sourceRef.id;

        // Find matching emoji from COMMON_SOURCE_TYPES
        const sourceTypeConfig = COMMON_SOURCE_TYPES.find(
          (config) => config.name === sourceConfig.source.name,
        );
        const emoji = sourceTypeConfig?.emoji;

        // Create source document
        await sourceRef.set({
          fieldIds: sourceConfig.source.fieldIds,
          fieldNames: sourceConfig.source.fieldNames,
          name: sourceConfig.source.name,
          ...(sourceConfig.source.defaultFields?.length && {
            defaultFields: sourceConfig.source.defaultFields,
          }),
          ...(sourceConfig.source.type && { type: sourceConfig.source.type }),
          ...(emoji && { emoji }),
          ...(sourceConfig.source.imageFieldIds?.length && {
            imageFieldIds: sourceConfig.source.imageFieldIds,
          }),
        });

        // Store source ID at the correct index to maintain alignment with sources array
        sourceIdByIndex[i] = sourceId;
        // Also add to createdSourceIds for session update
        createdSourceIds.push(sourceId);

        // Create rows if provided
        if (sourceConfig.rows.length > 0) {
          const rowsCollection = db.sessionSourceRows(newSessionRef.id, sourceId);
          // Pre-generate row IDs so we can track them for card connections
          const rowIds = sourceConfig.rows.map(() => ulidFactory());
          // Store row IDs for this source (used for card connections)
          rowIdsBySourceIndex[i] = rowIds;
          // Zip rows with their IDs for batching
          const rowsWithIds = sourceConfig.rows.map((row, idx) => ({ row, rowId: rowIds[idx] }));
          await safeBatch(firestoreDb, rowsWithIds, (batch, { row, rowId }) => {
            const rowDoc = rowsCollection.doc(rowId);
            batch.set(rowDoc, { fieldValues: row });
          });
        }

        // Don't create modules yet - wait until all sources and rows are created
        // Modules will be created in a batch after all sources are ready
      } catch (error: any) {
        errors.push(`Failed to create source "${sourceConfig.name}": ${error.message}`);
        // sourceIdByIndex[i] remains undefined for failed sources
      }
    }

    // Update session with all source IDs
    if (createdSourceIds.length > 0) {
      try {
        await newSessionRef.set(
          {
            sourceIds: FieldValue.arrayUnion(...createdSourceIds) as unknown as string[],
          },
          { merge: true },
        );
      } catch (error: any) {
        errors.push(`Failed to update session with source IDs: ${error.message}`);
      }
    }

    // Declare variables outside if block so they're accessible later
    const allModuleIds: string[] = [];
    const moduleDisplaySettings: Record<string, any> = {};
    let modulesToCreate: Record<string, any> = {};

    // Now create modules for all sources (after all sources and rows are created)
    if (sessionData.automaticModuleCreation) {
      const sourcesWithModules = new Set<number>();
      modulesToCreate = {};

      // First pass: Collect all REAL modules to create
      for (let i = 0; i < sources.length; i++) {
        const sourceConfig = sources[i];
        const sourceId = sourceIdByIndex[i];

        // Create REAL module if source has rows OR files to upload
        const hasRows = sourceConfig.rows.length > 0;
        const hasPdfFiles = !!(sourceConfig.pdfFiles && sourceConfig.pdfFiles.length > 0);
        const hasImageFiles = !!(sourceConfig.imageFiles && sourceConfig.imageFiles.length > 0);
        const hasData = hasRows || hasPdfFiles || hasImageFiles;

        if (hasData && sourceId) {
          const moduleId = ulid();
          createdModuleIds.push(moduleId);
          allModuleIds.push(moduleId);
          sourcesWithModules.add(i);
          const randomColor = moduleColors[Math.floor(Math.random() * moduleColors.length)];

          // Determine if source should use gallery behavior
          const isGallerySource =
            sourceConfig.source.type === 'image' ||
            hasImageFiles ||
            sourceConfig.source.displayMode === 'gallery';
          const fieldsToShow =
            sourceConfig.source.defaultFields && sourceConfig.source.defaultFields.length > 0
              ? sourceConfig.source.defaultFields
              : isGallerySource
              ? sourceConfig.source.fieldIds
              : sourceConfig.source.fieldIds.slice(0, 4);

          modulesToCreate[moduleId] = {
            id: moduleId,
            name: sourceConfig.source.name,
            color: randomColor,
            sourceId,
            multi: isGallerySource,
            table: true,
            fields: fieldsToShow,
            showThumbnails: true,
          };
        }
      }

      // Second pass: Collect all PLACEHOLDER modules to create
      for (let i = 0; i < sources.length; i++) {
        const sourceConfig = sources[i];
        const sourceId = sourceIdByIndex[i];

        // Skip if already has a real module
        if (sourcesWithModules.has(i)) {
          continue;
        }

        // Create placeholder module ONLY if source has no rows AND no files to upload
        const hasRows = sourceConfig.rows.length > 0;
        const hasPdfFiles = !!(sourceConfig.pdfFiles && sourceConfig.pdfFiles.length > 0);
        const hasImageFiles = !!(sourceConfig.imageFiles && sourceConfig.imageFiles.length > 0);
        const needsPlaceholder = !hasRows && !hasPdfFiles && !hasImageFiles;

        if (sourceId && needsPlaceholder) {
          const moduleId = ulid();
          allModuleIds.push(moduleId);
          const randomColor = moduleColors[Math.floor(Math.random() * moduleColors.length)];

          modulesToCreate[moduleId] = {
            id: moduleId,
            name: sourceConfig.source.name,
            color: randomColor,
            sourceId,
            multi: false,
            table: true,
            fields: [],
            showThumbnails: true,
            needsSetup: true,
          };
        }
      }

      // Write ALL modules at once with default display settings
      if (Object.keys(modulesToCreate).length > 0) {
        try {
          // Create default display settings for all modules (50% width)
          allModuleIds.forEach((moduleId) => {
            moduleDisplaySettings[moduleId] = {
              moduleBucketSize: 'Half', // 50% width
              connectionsDisplayMode: 'Grid',
            };
          });

          await newSessionRef.set(
            {
              modules: modulesToCreate,
              moduleIds: allModuleIds,
              moduleDisplaySettings,
            },
            { merge: true },
          );
        } catch (error: any) {
          console.error('Failed to write modules:', error);
          errors.push(`Failed to create modules: ${error.message}`);
        }
      }
    }

    // Create starter cards for the session
    // If preConnectFirstCard is true, connect each card to its corresponding row from each source
    try {
      const cardsCollection = db.sessionCards(newSessionRef.id);
      const cardIds: string[] = [];

      // Build a map of sourceId -> moduleId for quick lookup
      const moduleIdBySourceId: Record<string, string> = {};
      Object.entries(modulesToCreate).forEach(([moduleId, module]) => {
        moduleIdBySourceId[module.sourceId] = moduleId;
      });

      // Create cardCount cards, each connected to row[cardIndex] from each source
      for (let cardIndex = 0; cardIndex < cardCount; cardIndex++) {
        const cardRef = cardsCollection.doc();
        cardIds.push(cardRef.id);

        // Build connections for this card
        const connections: Record<string, string[]> = {};
        if (preConnectFirstCard) {
          // Connect to the corresponding row from each source
          for (let sourceIndex = 0; sourceIndex < sources.length; sourceIndex++) {
            const sourceId = sourceIdByIndex[sourceIndex];
            const rowIds = rowIdsBySourceIndex[sourceIndex];

            if (sourceId && rowIds && rowIds[cardIndex]) {
              const moduleId = moduleIdBySourceId[sourceId];
              if (moduleId) {
                connections[moduleId] = [rowIds[cardIndex]];
              }
            }
          }
        }

        await cardRef.set({
          connections,
          createdAt: FieldValue.serverTimestamp() as unknown as Date,
          createdBy: {
            userId: this.rootStore.authStore.user!.uid,
            email: this.rootStore.authStore.user!.email,
          },
          // Seeded sessions start cards in the first step (e.g. "Planned"), so the
          // Whole board and multi-step queue open with a populated pipeline.
          ...(steps && steps.length > 0 ? { stepId: steps[0].id } : {}),
        });
      }

      await newSessionRef.set(
        {
          cardIds: FieldValue.arrayUnion(...cardIds) as unknown as string[],
        },
        { merge: true },
      );
    } catch (error: any) {
      console.error('Failed to create cards:', error);
      // Don't fail the entire operation if card creation fails
    }

    // Create default layout preset
    try {
      const defaultPresetId = ulid();

      // Calculate hidden module IDs based on visibleSources
      // Map source names to module IDs, then exclude visible ones
      const hiddenModuleIds = allModuleIds.filter((moduleId) => {
        const module = modulesToCreate[moduleId];
        if (!module) return false;

        // Find the source name for this module
        const moduleSourceName = module.name;

        // If visibleSources is empty (no selection made), show all modules
        if (visibleSources.length === 0) {
          return false;
        }

        // Hide modules whose source is not in visibleSources
        return !visibleSources.includes(moduleSourceName);
      });

      const defaultPreset: LayoutPreset = {
        name: 'Default Layout',
        moduleIds: allModuleIds,
        visibility: {
          hiddenModuleIds,
          hideFilename: true, // Match session default
        },
        moduleDisplaySettings,
        connectionDisplaySettings: {}, // Default text sizes will be empty (uses user defaults)
        createdAt: new Date(),
      };

      await newSessionRef.update({
        [`layoutPresets.${defaultPresetId}`]: defaultPreset,
      });
    } catch (error: any) {
      console.error('Failed to create default preset:', error);
      // Don't fail the entire operation if preset creation fails
    }

    if (errors.length > 0 && createdSourceIds.length === 0) {
      // If all sources failed, delete the session
      try {
        await newSessionRef.delete();
      } catch (deleteError) {
        console.error('Failed to cleanup session after error:', deleteError);
      }
      return { errors };
    }

    return { sessionId: newSessionRef.id, errors: errors.length > 0 ? errors : undefined };
  };

  //
  /**
   * Handles common logic for importing session with source configured
   * Because we need to route to new session before operating on it, we have a persistent loading state across those two pages
   */
  importSession = async (onSessionCreation: (sessionId: string) => Promise<unknown>) => {
    let sessionId;
    try {
      this.importInProgress = true;
      const sessionName = this.rootStore.routerStore.routerState.queryParams.name;
      sessionId = await this.createNewSession({ name: sessionName });
      if (sessionId) {
        await onSessionCreation(sessionId);
      } else {
        throw new Error('No session id');
      }
    } catch (error) {
      // On error, we should try to cleanup any created state
      if (sessionId) {
        this.deleteSession(sessionId);
        await this.rootStore.routerStore.goTo(RouteNames.HOME);
        sessionId = undefined;
      }
    } finally {
      this.importInProgress = false;
    }
  };

  /**
   * Duplicates a session with the given options.
   * Creates a new session with copied sources, modules, cards, etc. based on options.
   *
   * @throws Error if user is not authenticated
   * @throws Error if the cloud function fails
   */
  duplicateSession = async (
    sessionId: string,
    options: {
      name: string;
      sources: boolean;
      sourceRows: boolean;
      modules: boolean;
      filenameFormula: boolean;
      queueCards: boolean;
      resetCardStatus: boolean;
    },
  ): Promise<{
    newSessionId: string;
    summary?: {
      sourcesCopied: number;
      rowsCopied: number;
      modulesCopied: number;
      cardsCopied: number;
    };
  }> => {
    if (!this.rootStore.authStore.user) {
      throw new Error('User must be authenticated to duplicate a session');
    }
    trackAction('session_duplicated');

    // Validate options before sending to cloud function
    if (!options.name || options.name.trim().length === 0) {
      throw new Error('Session name is required');
    }

    // Validate option dependencies
    if (options.sourceRows && !options.sources) {
      throw new Error('Cannot copy source rows without copying sources');
    }
    if (options.modules && !options.sources) {
      throw new Error('Cannot copy modules without copying sources');
    }
    if (options.filenameFormula && !options.modules) {
      throw new Error('Cannot copy filename formula without copying modules');
    }
    if (options.queueCards && !options.modules) {
      throw new Error('Cannot copy cards without copying modules');
    }
    if (options.queueCards && !options.sourceRows) {
      throw new Error('Cannot copy cards without copying source rows');
    }

    try {
      const response = await fn.httpsCallable('duplicateSession')({
        sessionId,
        ...options,
      });

      const data = response.data as {
        ok: boolean;
        newSessionId: string;
        summary?: {
          sourcesCopied: number;
          rowsCopied: number;
          modulesCopied: number;
          cardsCopied: number;
        };
        warnings?: string[];
      };

      if (!data.ok || !data.newSessionId) {
        throw new Error('Failed to duplicate session: Invalid response from server');
      }

      // Log any warnings from the server
      if (data.warnings && data.warnings.length > 0) {
        console.warn('Duplicate session warnings:', data.warnings);
      }

      // Navigate to the new session
      await this.rootStore.routerStore.goTo(RouteNames.SESSION_QUEUE, {
        params: { sessionId: data.newSessionId },
      });

      return {
        newSessionId: data.newSessionId,
        summary: data.summary,
      };
    } catch (error) {
      // Re-throw the error so the UI can handle it
      // Firebase errors have a message property that we can use
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred while duplicating the session');
    }
  };
}
